/**
 * StationRenderPage — headless render surface for the Print Station EXE.
 *
 * The station's hidden BrowserWindow loads this route, then calls
 * window.__stationRender(payload) with the render-data it pulled from
 * /print-receivers/render-data (+ the chosen format and this station's paper
 * calibration). The page renders the document with the LIVE PrintEngine and
 * writes it into itself; the EXE then silent-prints the window.
 *
 * THERMAL = BITMAP (owner-confirmed twice: web 2026-07-15, station paper
 * 2026-08-11 "not as thick as when I print it myself"). Vector text handed to
 * a thermal driver anti-aliases into thin grey strokes; the web solved this by
 * printing a rasterized receipt, so the station now shares that exact capture
 * (PrintEngine._captureThermalStrips) instead of writing raw HTML. Rasterizing
 * also inlines the QR as a data URL, which fixes receipts that printed with a
 * missing QR when api.qrserver.com lost the race with silent print.
 *
 * Calibration comes from the STATION (paper width + font delta) and is written
 * into localStorage so the engine's own desktop-raster hooks read it — same
 * code path, same numbers, same output as a calibrated web print.
 *
 * No auth and NO data fetching here — the page renders only what it is handed.
 */
import { useEffect } from 'react';
import PrintEngine from '../lib/PrintEngine';

export default function StationRenderPage() {
  useEffect(() => {
    window.__stationRender = async (payload) => {
      const { document_type, document: doc, business_info, doc_code, format } = payload || {};
      if (!doc) return 'error: no document';
      let fmt = format || 'thermal';
      let renderDoc = doc;
      let type =
        document_type === 'purchase_order'   ? 'purchase_order' :
        document_type === 'branch_transfer'  ? 'branch_transfer' :
        document_type === 'proof_of_payment' ? 'proof_of_payment' :
        document_type === 'expense'          ? 'expense_voucher' :
        document_type === 'packing_slip'     ? 'packing_slip' :   // price-free trucker copy
        document_type === 'stock_request'    ? 'stock_request' :  // new-flow /stock-requests doc
        (PrintEngine.getDocType(doc) || 'order_slip');   // invoice family
      if (document_type === 'capital_change_report') {
        // Report SNAPSHOT (2026-09-03): the pulled doc wraps the frozen report in
        // `data`; `kind` picks the layout and pins the format — a strip only has
        // a thermal template, a sheet only a dot-matrix one.
        type = doc.kind === 'strip' ? 'capital_price_strip' : 'capital_change_report';
        renderDoc = doc.data || {};
        fmt = doc.kind === 'strip' ? 'thermal' : 'dot_matrix';
      }

      // Calibration: by DEFAULT send nothing, so the engine's own defaults
      // (80 mm layout, +2 font delta = the owner's 2026-07-16 web tuning)
      // apply and the station prints pixel-identical to a calibrated web
      // print — the 80 mm bitmap shrinks onto the real roll via max-width,
      // exactly like the web popup (0.2.6 lesson: overriding with the true
      // roll width + delta 0 made prints ~15% smaller than the web's).
      // Explicit payload values are OVERRIDES; absent values must CLEAR any
      // previously-set keys — this window's localStorage persists across jobs.
      try {
        if (Number.isFinite(Number(payload.paper_width_mm))) {
          localStorage.setItem('agribooks_desktop_paper_width_mm', String(Number(payload.paper_width_mm)));
        } else {
          localStorage.removeItem('agribooks_desktop_paper_width_mm');
        }
        if (Number.isFinite(Number(payload.font_delta_px))) {
          localStorage.setItem('agribooks_desktop_font_delta_px', String(Number(payload.font_delta_px)));
        } else {
          localStorage.removeItem('agribooks_desktop_font_delta_px');
        }
      } catch { /* private mode — engine defaults apply */ }

      const html = PrintEngine.generateHtml({
        type, data: renderDoc, format: fmt,
        businessInfo: business_info || {}, docCode: doc_code || '',
      });
      if (!html) return 'error: empty html';

      if (fmt === 'thermal') {
        try {
          const { stripUrls, paperMm } = await PrintEngine._captureThermalStrips({ fullHtml: html });
          document.open();
          document.write(
            `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Receipt</title>` +
            `<style>@page{margin:0;} *{margin:0;padding:0;box-sizing:border-box;} ` +
            `html,body{width:100%;background:#fff;} ` +
            `img{display:block;width:${paperMm}mm;max-width:100%;background:#fff;` +
            `break-inside:avoid;page-break-inside:avoid;}</style></head><body>` +
            stripUrls.map((u) => `<img src="${u}" alt="receipt"/>`).join('') +
            `</body></html>`
          );
          document.close();
          // The station waits on this promise, so the bitmap is fully decoded
          // before it prints — a partially-decoded PNG prints thin/uneven.
          await Promise.all(Array.from(document.images).map((img) =>
            (img.decode ? img.decode().catch(() => {}) : Promise.resolve())));
          return 'ok';
        } catch (e) {
          // Raster failed — fall through to vector so a receipt still prints.
          // eslint-disable-next-line no-console
          console.warn('[station] thermal raster failed → vector fallback:', e);
        }
      }

      document.open();
      document.write(html);
      document.close();
      await Promise.all(Array.from(document.images).map((img) =>
        (img.decode ? img.decode().catch(() => {}) : Promise.resolve())));
      return 'ok';
    };
  }, []);

  return (
    <div style={{ fontFamily: 'monospace', padding: 24, color: '#64748b', fontSize: 13 }}
      data-testid="station-render-ready">
      AgriBooks Print Station render surface — waiting for job payload.
    </div>
  );
}
